<template>
  <KeepAlive>
    <div class="overflow-y-auto" ref="applicationsTable">
      <!-- Loading with no data: show skeleton -->
      <div v-if="isLoadingAny && !applications?.length" class="p-4">
        <div class="animate-pulse">
          <div class="h-4 bg-gray-200 rounded w-full mb-4"></div>
          <div v-for="n in 6" :key="n" class="h-12 bg-gray-200 rounded w-full mb-2"></div>
        </div>
      </div>

      <!-- No applications message -->
      <div
        v-else-if="!isLoadingAny && !applications?.length"
        class="flex flex-col items-center justify-center py-12 px-4 text-center"
      >
        <HeartCrack class="w-12 h-12 text-gray-400 mb-4" />
        <h3 class="text-lg font-medium text-gray-900 mb-1">
          {{ isCurrentUser ? 'No applications yet' : 'No applications found' }}
        </h3>
        <p class="text-gray-500 max-w-sm">
          <span v-if="isCurrentUser">
            Start your journey by applying to open positions <br />
            <a href="/roles" class="text-blue-500 hover:underline">on the job postings page</a>
          </span>
          <span v-else> This user hasn't applied to any positions yet. </span>
        </p>
      </div>

      <!-- Applications table -->
      <!-- Data present: show table; if loading, dim and disable interactions -->
      <div v-else class="w-full overflow-x-auto transition-all duration-300" :class="{ 'opacity-50 pointer-events-none': isLoading }">
        <table class="w-full divide-y divide-gray-200" :class="canSeeReferralColumns ? 'min-w-[1400px]' : 'min-w-[1000px]'">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th
                v-if="!isMinimized"
                class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[100px]"
                @click="handleSort('updated_at')"
              >
                <div class="flex items-center">
                  Updated
                  <TableSortIndicator field="updated_at" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                :class="canSeeReferralColumns ? 'min-w-[250px] max-w-[400px] w-[350px]' : 'min-w-[250px] max-w-[400px] w-[380px]'"
                @click="handleSort('open_role__role_name')"
              >
                <div class="flex items-center">
                  Role & Company
                  <TableSortIndicator field="open_role__role_name" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!isMinimized"
                class="py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('open_role__employer__name')"
              >
                <div class="flex items-center">
                  Employer
                  <TableSortIndicator field="open_role__employer__name" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!isMinimized"
                class="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('interview_status')"
              >
                <div class="flex items-center">
                  Interview Stage
                  <TableSortIndicator field="interview_status" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!isMinimized"
                class="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('application_status')"
              >
                <div class="flex items-center">
                  Application Status
                  <TableSortIndicator field="application_status" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!isMinimized && canSeeReferralColumns"
                class="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('referral_type')"
              >
                <div class="flex items-center">
                  Referral Type
                  <TableSortIndicator field="referral_type" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!isMinimized && canSeeReferralColumns"
                class="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('referral_status')"
              >
                <div class="flex items-center">
                  Referral Status
                  <TableSortIndicator field="referral_status" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="application in sortedApplications"
              :key="application.id"
              class="hover:bg-gray-50"
              @click="navigateToApplication(application.id)"
            >
              <!-- Updated Date -->
              <td
                v-if="!isMinimized"
                class="px-2 py-4 whitespace-nowrap text-gray-900 text-xs cursor-pointer w-[100px]"
                @click="navigateToApplication(application.id)"
              >
                {{ formatDate(application.updated_at) }}
              </td>

              <!-- Role & Company -->
              <td
                class="pl-4 pr-4 py-4 cursor-pointer overflow-hidden"
                :class="canSeeReferralColumns ? 'min-w-[250px] max-w-[400px] w-[350px]' : 'min-w-[250px] max-w-[400px] w-[380px]'"
                @click="navigateToApplication(application.id)"
              >
                <div class="flex items-center min-w-0">
                  <img
                    v-if="application.open_role_details?.employer_details?.logo"
                    :src="application.open_role_details?.employer_details?.logo"
                    class="h-10 w-10 rounded-lg mr-4 flex-shrink-0"
                  />
                  <div
                    v-else
                    class="h-10 w-10 rounded-lg bg-gray-200 mr-4 flex items-center justify-center flex-shrink-0"
                  >
                    <Building2 class="h-5 w-5 text-gray-400" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div
                      class="text-sm font-medium text-gray-900 truncate max-w-[350px]"
                      :title="application.open_role_details?.role_name"
                    >
                      {{ application.open_role_details?.role_name }}
                    </div>
                    <div class="flex items-center gap-2 min-w-0">
                      <!-- Location data-->
                      <div
                        v-if="application.open_role_details?.locations_details?.length"
                        class="flex items-center gap-2 min-w-0"
                      >
                        <div
                          class="flex items-center border-[1px] rounded-full py-0.5 px-2 text-xs flex-shrink-0"
                        >
                          <MapPin class="w-4 h-4 mr-1 text-gray-400" />
                          <span class="truncate">
                            {{ application.open_role_details.locations_details[0].location_name }}
                          </span>
                        </div>

                        <div
                          v-if="application.open_role_details.locations_details.length > 1"
                          class="text-xs text-gray-500 flex-shrink-0"
                        >
                          +{{ application.open_role_details.locations_details.length - 1 }}
                        </div>
                        <RemoteBadge v-if="application.open_role_details?.is_remote" />
                      </div>
                    </div>
                  </div>
                </div>
              </td>

              <!-- Employer -->
              <td v-if="!isMinimized" class="whitespace-nowrap" @click.stop>
                <div class="text-sm text-gray-500 truncate">
                  {{ application.open_role_details?.employer_details?.name }}
                </div>
              </td>

              <!-- Interview Stage -->
              <td v-if="!isMinimized" class="whitespace-nowrap" @click.stop>
                <Select
                  v-if="isOwner"
                  :model-value="application.interview_status"
                  @update:model-value="
                    (value) => handleStatusChange(application.id, 'interview_status', value)
                  "
                >
                  <SelectTrigger class="border-0 shadow-none hover:bg-gray-100 w-[190px]">
                    <SelectValue>
                      <span
                        :class="getStatusClasses('interview', application.interview_status)"
                        class="px-2 py-1 rounded-full text-xs truncate max-w-[150px] flex items-center"
                      >
                        <Loader2
                          v-if="statusLoading[`${application.id}-interview_status`]"
                          class="w-4 h-4 animate-spin mr-2"
                        />
                        {{ getStatusLabel('interview', application.interview_status) }}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent class="z-[9999]">
                    <SelectItem
                      v-for="status in interviewStatusOptions"
                      :key="status.value"
                      :value="status.value"
                    >
                      <span
                        :class="[status.bgColor, status.textColor]"
                        class="px-2 py-1 rounded-full text-xs"
                      >
                        {{ status.label }}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div v-else class="px-2">
                  <span
                    :class="getStatusClasses('interview', application.interview_status)"
                    class="px-2 py-1 rounded-full text-xs truncate max-w-[150px] block"
                    :title="getStatusLabel('interview', application.interview_status)"
                  >
                    {{ getStatusLabel('interview', application.interview_status) }}
                  </span>
                </div>
              </td>

              <!-- Application Status -->
              <td v-if="!isMinimized" class="whitespace-nowrap" @click.stop>
                <Select
                  v-if="isOwner"
                  :model-value="application.application_status ?? ''"
                  @update:model-value="
                    (value) => handleStatusChange(application.id, 'application_status', value)
                  "
                >
                  <SelectTrigger class="border-0 shadow-none hover:bg-gray-100 w-[190px]">
                    <SelectValue>
                      <span
                        :class="getStatusClasses('application', application.application_status)"
                        class="px-2 py-1 rounded-full text-xs truncate max-w-[150px] flex items-center"
                      >
                        <Loader2
                          v-if="statusLoading[`${application.id}-application_status`]"
                          class="min-w-3 w-3 min-h-3 h-3 animate-spin mr-1"
                        />
                        {{ getStatusLabel('application', application.application_status) }}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent class="z-[9999]">
                    <SelectItem
                      v-for="status in applicationStatusOptions"
                      :key="status.value"
                      :value="status.value"
                    >
                      <span
                        :class="[status.bgColor, status.textColor]"
                        class="px-2 py-1 rounded-full text-xs"
                      >
                        {{ status.label }}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div v-else class="px-2">
                  <span
                    :class="getStatusClasses('application', application.application_status)"
                    class="px-2 py-1 rounded-full text-xs truncate max-w-[150px] block"
                    :title="getStatusLabel('application', application.application_status)"
                  >
                    {{ getStatusLabel('application', application.application_status) }}
                  </span>
                </div>
              </td>

              <!-- Referral Type -->
              <td
                v-if="!isMinimized && canSeeReferralColumns"
                class="whitespace-nowrap"
                @click.stop
              >
                <Select
                  v-if="isOwner"
                  :model-value="application.referral_type"
                  @update:model-value="
                    (value) => handleStatusChange(application.id, 'referral_type', value)
                  "
                >
                  <SelectTrigger class="border-0 shadow-none hover:bg-gray-100 w-[190px]">
                    <SelectValue>
                      <span
                        :class="getStatusClasses('referralType', application.referral_type)"
                        class="px-2 py-1 rounded-full text-xs truncate max-w-[150px] flex items-center"
                      >
                        <Loader2
                          v-if="statusLoading[`${application.id}-referral_type`]"
                          class="w-4 h-4 animate-spin mr-2"
                        />
                        {{ getStatusLabel('referralType', application.referral_type) }}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent class="z-[9999]">
                    <SelectItem
                      v-for="status in referralTypeOptions"
                      :key="status.value"
                      :value="status.value"
                    >
                      <span
                        :class="[status.bgColor, status.textColor]"
                        class="px-2 py-1 rounded-full text-xs"
                      >
                        {{ status.label }}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div v-else class="px-2">
                  <span
                    :class="getStatusClasses('referralType', application.referral_type)"
                    class="px-2 py-1 rounded-full text-xs truncate max-w-[150px] block"
                    :title="getStatusLabel('referralType', application.referral_type)"
                  >
                    {{ getStatusLabel('referralType', application.referral_type) }}
                  </span>
                </div>
              </td>

              <!-- Referral Status -->
              <td
                v-if="!isMinimized && canSeeReferralColumns"
                class="whitespace-nowrap"
                @click.stop
              >
                  <Select
                  v-if="isOwner || isGuide"
                  :model-value="application.referral_status"
                  :disabled="isReferralStatusDisabled(application)"
                  @update:model-value="
                    (value) => handleStatusChange(application.id, 'referral_status', value)
                  "
                >
                  <SelectTrigger
                    :disabled="isReferralStatusDisabled(application)"
                    :class="['border-0 shadow-none w-[190px]', !isReferralStatusDisabled(application) && 'hover:bg-gray-100']"
                    :title="application.referral_tier_allowed === false ? REFERRAL_TIER_UNAVAILABLE_MESSAGE : undefined"
                  >
                    <SelectValue>
                      <span
                        :class="getStatusClasses('referralStatus', application.referral_status)"
                        class="px-2 py-1 rounded-full text-xs truncate max-w-[150px] flex items-center"
                      >
                        <Loader2
                          v-if="statusLoading[`${application.id}-referral_status`]"
                          class="w-4 h-4 animate-spin mr-2"
                        />
                        {{ getReferralStatusLabel(application) }}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent class="z-[9999]">
                    <SelectItem
                      v-for="status in filteredReferralStatusOptions"
                      :key="status.value"
                      :value="status.value"
                    >
                      <span
                        :class="[status.bgColor, status.textColor]"
                        class="px-2 py-1 rounded-full text-xs"
                      >
                        {{ status.label }}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div v-else class="px-2">
                  <span
                    :class="getStatusClasses('referralStatus', application.referral_status)"
                    class="px-2 py-1 rounded-full text-xs truncate max-w-[150px] block"
                    :title="getReferralStatusLabel(application)"
                  >
                    {{ getReferralStatusLabel(application) }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <PaginationComponent
        v-if="applications?.length"
        :current-page="currentPage"
        :total-pages="totalPages"
        :use-router="!props.fetchApplications"
        :additional-query-params="{
          ...route.query,
          ordering: applicationsStore.currentSort
        }"
        @update:page="handlePageChange"
      />
    </div>
  </KeepAlive>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApplicationsStore } from '@/stores/applications'
import { useUserStore } from '@/stores/user'
import {
  HeartCrack,
  Building2,
  MapPin,
  Loader2
} from 'lucide-vue-next'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import TableSortIndicator from '@/components/common/TableSortIndicator.vue'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  applicationStatusOptions,
  interviewStatusOptions,
  referralTypeOptions,
  referralStatusOptions,
  REFERRAL_TIER_UNAVAILABLE_MESSAGE
} from '@/const/applicationStatuses'
import { useToast } from '@/components/ui/toast/use-toast'
import RemoteBadge from '@/components/common/badges/RemoteBadge.vue'
import type { Application } from '@/types/application'
import { useAuth } from '@/composables/useAuth'

interface Props {
  userId?: string
  applications?: Application[]
  currentPage?: number
  totalPages?: number
  isLoading?: boolean
  showPagination?: boolean
  fetchApplications?: (page: number) => Promise<void>
  onStatusUpdate?: (applicationId: number, field: string, value: string) => Promise<void>
}

const props = withDefaults(defineProps<Props>(), {
  currentPage: 1,
  totalPages: 1,
  isLoading: false,
  showPagination: true
})

const { isGuide, isLimitedUser } = useAuth()
const emit = defineEmits<{
  openApplicationDetails: [applicationId: number]
}>()

const applicationsStore = useApplicationsStore()
const userStore = useUserStore()
const route = useRoute()
const router = useRouter()
const { toast } = useToast()

const applicationsTable = ref<HTMLDivElement | null>(null)
const isPaginationLoading = ref(false)
const statusLoading = ref<Record<string, boolean>>({})

// Unified loading state: prop-driven, store-driven, or internal pagination
const isLoadingAny = computed(() => {
  const propLoading = !!props.isLoading
  const storeLoading = applicationsStore.isLoadingList
  return propLoading || storeLoading || isPaginationLoading.value
})

// Check if we're looking at the current user's applications
const isCurrentUser = computed(() => {
  if (!props.userId || !userStore.currentUser?.id) return false
  return props.userId === userStore.currentUser.id.toString()
})

const isOwner = computed(() => {
  return props.userId === userStore.currentUser?.id?.toString()
})

// Referral columns are hidden for limited users (Common / unsupported Fellows)
const canSeeReferralColumns = computed(
  () => !isLimitedUser.value && (isGuide.value || isOwner.value === true)
)

// Guides see all referral status options; owners only see non-internal ones
const filteredReferralStatusOptions = computed(() => {
  if (isGuide.value) return referralStatusOptions
  return referralStatusOptions.filter((opt) => !opt.internal)
})

const isReferralStatusDeadend = (status: string | null) =>
  !!referralStatusOptions.find((opt) => opt.value === status)?.deadend

const isReferralStatusDisabled = (application: Application) => {
  if (isReferralStatusDeadend(application.referral_status)) return true
  if (
    application.referral_status === 'NOT_STARTED' &&
    (application.referrers_exist_for_employer === false ||
      application.referral_tier_allowed === false) &&
    !isGuide.value
  ) return true
  return false
}

const getReferralStatusLabel = (application: Application) => {
  if (
    isOwner.value &&
    application.referral_status === 'NOT_STARTED' &&
    application.referral_tier_allowed === false
  ) return 'Tier unavailable'
  if (
    isOwner.value &&
    application.referral_status === 'NOT_STARTED' &&
    application.referrers_exist_for_employer === false
  ) return 'No referrers'
  return getStatusLabel('referralStatus', application.referral_status)
}

// Check if an application is selected (minimized view)
const isMinimized = computed(() => {
  return !!route.params.application_id
})

// Use prop applications if provided, otherwise fall back to store
const applications = computed(() => {
  return props.applications || applicationsStore.userApplications || []
})

// Use shared pagination state when available
const currentPage = computed(() => {
  return props.currentPage || applicationsStore.currentPage || 1
})

const totalPages = computed(() => {
  return (
    props.totalPages ||
    Math.ceil(applicationsStore.userTotalCount / applicationsStore.pageSize) ||
    1
  )
})

// Backend handles sorting, so we just return applications as-is
const sortedApplications = computed(() => {
  return applications.value || []
})

const isDeadEndApplication = (application: Application) => {
  const s = application.application_status
  return s != null && ['POSITION_CLOSED', 'REJECTED', 'CANCELLED', 'OFFER_SIGNED'].includes(s)
}

const handleSort = async (field: string) => {
  // Toggle sort direction
  const currentSortValue = applicationsStore.currentSort
  const newSort = currentSortValue === field
    ? `-${field}`
    : currentSortValue === `-${field}`
      ? field
      : field // Default to ascending on first click

  // Update store immediately so chevrons react instantly
  applicationsStore.currentSort = newSort
  applicationsStore.currentPage = 1
  applicationsStore.lastRequestedPage = null

  // Update URL (do not await to avoid blocking UI reactivity)
  router.replace({
    query: {
      ...route.query,
      ordering: newSort,
      page: '1'
    }
  }).catch(() => {})

  // Trigger data fetching depending on mode
  if (props.fetchApplications) {
    await props.fetchApplications(1)
  } else if (isCurrentUser.value) {
    await applicationsStore.fetchUserApplications(1, applicationsStore.pageSize)
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)

  // Check if the date is March 6, 2025
  if (date.getFullYear() === 2025 && date.getMonth() === 2 && date.getDate() === 6) {
    return '-'
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

// Helper function to get status label
const getStatusLabel = (
  type: 'application' | 'interview' | 'referralStatus' | 'referralType',
  value: string | null
) => {
  if (!value) return 'Not set'

  const options = {
    application: applicationStatusOptions,
    interview: interviewStatusOptions,
    referralStatus: referralStatusOptions,
    referralType: referralTypeOptions
  }[type]

  return options.find((option) => option.value === value)?.label || value
}

// Helper function to get option classes
const getStatusClasses = (
  type: 'application' | 'interview' | 'referralStatus' | 'referralType',
  value: string | null
) => {
  if (!value) return 'bg-gray-100 text-gray-700'

  const options = {
    application: applicationStatusOptions,
    interview: interviewStatusOptions,
    referralStatus: referralStatusOptions,
    referralType: referralTypeOptions
  }[type]

  const option = options.find((option) => option.value === value)
  return option ? [option.bgColor, option.textColor] : ['bg-gray-100', 'text-gray-700']
}

// Handle status changes
const handleStatusChange = async (
  applicationId: number,
  field: 'application_status' | 'referral_status' | 'interview_status' | 'referral_type',
  value: any
) => {
  if (typeof value !== 'string') return

  const loadingKey = `${applicationId}-${field}`
  if (statusLoading.value[loadingKey]) return
  statusLoading.value[loadingKey] = true
  try {
    // Use custom handler if provided, otherwise use store
    if (props.onStatusUpdate) {
      await props.onStatusUpdate(applicationId, field, value)
    } else {
      const updateData = {
        [field]: value
      }

      await applicationsStore.updateApplication(applicationId.toString(), updateData)

      // Update the local application in the list
      const appIndex = applicationsStore.userApplications.findIndex(
        (app) => app.id === applicationId
      )
      if (appIndex !== -1) {
        applicationsStore.userApplications[appIndex] = {
          ...applicationsStore.userApplications[appIndex],
          [field]: value
        }
      }
    }

    toast({
      title: 'Status updated',
      description: `Successfully updated ${field.replace('_', ' ')}`
    })
  } catch (error: any) {
    toast({
      title: 'Update failed',
      description: error.response?.data?.message || 'Failed to update status',
      variant: 'destructive'
    })
  } finally {
    statusLoading.value[loadingKey] = false
  }
}

const navigateToApplication = async (applicationId: number) => {
  try {
    if (isOwner.value) {
      // For current user applications, navigate directly like UserApplicationCard does
      const navigationOptions = {
        name: 'User Application container',
        params: { application_id: String(applicationId) },
        query: { ...route.query }
      }

      if (route.params.application_id) {
        await router.replace(navigationOptions)
      } else {
        await router.push(navigationOptions)
      }
    } else if (isGuide.value) {
      // For guides viewing other users' applications, emit event
      emit('openApplicationDetails', applicationId)
    } else {
      // Find the specific application that was clicked
      const clickedApplication = applications.value.find((app) => app.id === applicationId)

      if (clickedApplication?.open_role_details?.id) {
        // Navigate to job details for non-Guide users
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push({
          path: `/jobs/${clickedApplication.open_role_details.id}`,
          query: {
            ...route.query,
            from: 'applications',
            applicationId: applicationId
          }
        })
      } else {
        // Fallback: emit event to open modal if no role details found
        emit('openApplicationDetails', applicationId)
      }
    }
  } catch (error) {
    console.error('Navigation error:', error)
    // Fallback navigation for current user
    if (isCurrentUser.value) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push('/applications')
    }
  }
}

const scrollToTop = () => {
  applicationsTable.value?.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const handlePageChange = async (page: number) => {
  if (props.fetchApplications) {
    // Use custom fetch function if provided (for standalone usage like ApplicantView)
    await props.fetchApplications(page)
  } else if (isCurrentUser.value) {
    // For current user applications, use router navigation (shared state)
    await router.push({
      query: {
        ...route.query,
        page: page.toString()
      }
    })
  }
}

// Handle pagination for current user applications only when using store data directly
onMounted(async () => {
  if (isCurrentUser.value && !props.fetchApplications) {
    // Only fetch if we're showing current user's applications and no custom pagination provided
    const pageFromUrl = route.query.page ? parseInt(route.query.page as string) : 1
    const currentQuery = route.query

    // Apply filters from URL
    if (currentQuery.status) {
      applicationsStore.selectedStatus = currentQuery.status as string
    }

    // Apply sorting from URL
    if (currentQuery.ordering && typeof currentQuery.ordering === 'string') {
      applicationsStore.currentSort = currentQuery.ordering
    }

    isPaginationLoading.value = true

    try {
      applicationsStore.lastRequestedPage = null
      await applicationsStore.fetchUserApplications(pageFromUrl, applicationsStore.pageSize)
    } finally {
      isPaginationLoading.value = false
    }
  }
})

// Watch for route changes to handle pagination when showing current user's applications without props
watch(
  () => ({
    page: route.query.page,
    application_id: route.params.application_id,
    status: route.query.status,
    ordering: route.query.ordering
  }),
  // eslint-disable-next-line complexity
  async (newQuery, oldQuery) => {
    if (!isCurrentUser.value || props.fetchApplications) return

    const requestedPage = newQuery.page ? parseInt(newQuery.page as string) : 1
    const isFirstLoad = !oldQuery
    const isPageChanged = newQuery.page !== oldQuery?.page
    const isNavBack = oldQuery?.application_id && !newQuery.application_id
    const isFilterChanged = newQuery.status !== oldQuery?.status
    const isSortChanged = newQuery.ordering !== oldQuery?.ordering

    // Update sort from URL if it changed
    if (newQuery.ordering && typeof newQuery.ordering === 'string') {
      if (newQuery.ordering !== applicationsStore.currentSort) {
        applicationsStore.currentSort = newQuery.ordering
      }
    }

    if (isPageChanged || isFirstLoad || isFilterChanged || isSortChanged) {
      isPaginationLoading.value = true
    }

    applicationsStore.currentPage = requestedPage

    if (isPageChanged || isFilterChanged || isSortChanged) {
      applicationsStore.lastRequestedPage = null
    }

    if (isFirstLoad || isPageChanged || isNavBack || isFilterChanged || isSortChanged) {
      try {
        await applicationsStore.fetchUserApplications(requestedPage, applicationsStore.pageSize)

        if ((isPageChanged || isFilterChanged || isSortChanged) && !isFirstLoad) {
          scrollToTop()
        }
      } finally {
        isPaginationLoading.value = false
      }
    } else {
      isPaginationLoading.value = false
    }
  },
  { immediate: false }
)
</script>
